<template>
  <div>
    <BaseDialog
        title="O-Pluxイベント更新"
        width="700px"
        :visible.sync="dialogVisible"
        :show-only-close-button="false"
        :disable-ok-button="disabledOk"
        @closed="cancel"
        @cancel="cancel"
        @ok="ok">
      <template slot="contents">
        <div class="dialog-contents">
          <table class="detail-table">
            <template v-for="(prop, i) in tableSetList">
              <tr :key="i">
                <th :class="prop.class">
                  {{ prop.label }}
                </th>
                <td>
                  <template v-if="prop.path==='node.redResult'">
                    <BaseCell
                        :properties="prop"
                        :display-item="replicaData" />
                  </template>
                  <template v-else-if="['redResultReviewed', 'redBlack'].includes(prop.attrId)">
                    <BaseValidSelect
                        v-validate="prop.validateRule"
                        :data-vv-name="prop.attrId"
                        :data-vv-scope="validateScope"
                        :data-vv-as="prop.validateAs"
                        :options="getSelectOptions(prop)"
                        :value="prop.path(prop)"
                        :name="prop.attrId"
                        popper-class="front-dialog"
                        class="inputSelect"
                        size="mini"
                        @input="onChange($event, prop)" />
                    <div v-show="showErrorMessage(prop.attrId)"
                        style="display:block"
                        class="is-error-msg">
                      {{ errorMessage(prop.attrId) }}
                    </div>
                  </template>
                  <template v-else-if="prop.attrId==='redResultReviewedMemo'">
                    <BaseValidInput ref="input"
                        v-validate="prop.validateRule"
                        :data-vv-name="prop.attrId"
                        :data-vv-scope="validateScope"
                        :data-vv-as="prop.validateAs"
                        :value="prop.path(prop)"
                        :rows="5"
                        :name="prop.attrId"
                        width="400px"
                        type="textarea"
                        @input="onChange($event, prop)" />
                    <div v-show="showErrorMessage((prop.attrId))"
                        style="display:block"
                        class="is-error-msg">
                      {{ errorMessage(prop.attrId) }}
                    </div>
                  </template>
                  <template v-else>
                    {{ prop.path(prop) }}
                  </template>
                </td>
              </tr>
            </template>
          </table>
        </div>
      </template>
    </BaseDialog>
  </div>
</template>

<style scoped>
.dialog-contents ::v-deep table {
  width: auto;
}
.dialog-contents ::v-deep table tr:hover > td {
  background-color: initial;
}
.dialog-contents .detail-table ::v-deep th,
.dialog-contents .detail-table ::v-deep td {
  padding: 5px 15px;
  font-size: 14px;
  text-align: left;
  vertical-align: top;
}
.dialog-contents .detail-table ::v-deep th {
  min-width: 130px;
  border-bottom: initial;
}
.dialog-contents .detail-table ::v-deep th.required:after {
  content: " *";
  color: #ff5a5a;
  font-size: 85%;
}
.dialog-contents .detail-table ::v-deep td {
  width: 300px;
}
</style>
<script>

import { mapMutations } from 'vuex'
import _ from 'lodash'

import x from '@/components/mixins/mixin'
import * as config from '@/config'
import common from '@/utils/common'
import message from '@/utils/message'

import BaseDialog from '@/components/molecules/BaseDialog'
import BaseCell from '@/components/molecules/BaseCell'
import BaseValidSelect from '@/components/atoms/BaseValidSelect'
import BaseValidInput from '@/components/atoms/BaseValidInput'

export default {
  name: 'OpluxDialog',
  components: {
    BaseDialog,
    BaseCell,
    BaseValidSelect,
    BaseValidInput
  },
  mixins: [x],
  provide () {
    return {
      parentValidator: this.$validator
    }
  },
  props: {
    visible: {
      type: Boolean,
      required: true
    },
    opluxData: {
      type: Object,
      default: () => {}
    }
  },
  data () {
    return {
      dialogVisible: false,
      replicaData: {}
    }
  },
  computed: {
    validateScope () {
      return 'OpluxDialog'
    },
    hasError () {
      return this.errors.any(this.validateScope)
    },
    uuid () {
      return (this.opluxData.node || {}).uuid
    },
    lastUpdatedAt () {
      return (this.opluxData.node || {}).lastUpdatedAt
    },
    disabledOk () {
      return !Object.keys(this.requestBody).length
    },
    tableSetList () {
      return [
        { label: 'イベントID', path: this.getAttrValue, attrId: 'redEventId' },
        { label: '決済ステータス', path: this.getAttrValue, attrId: 'redPaymentStatus' },
        { label: '審査結果', path: 'node.redResult', type: 'String', codeName: 'd_order.red_result' },
        { label: '目視審査結果', path: this.getAttrValue, attrId: 'redResultReviewed', class: 'required',
          validateRule: 'required', validateAs: '目視審査結果' },
        { label: '目視審査結果メモ', path: this.getAttrValue, attrId: 'redResultReviewedMemo',
          validateRule: 'max:500', validateAs: '目視審査結果メモ' },
        { label: 'ブラックフラグ', path: this.getAttrValue, attrId: 'redBlack' },
        { label: '処理結果', path: this.getAttrValue, attrId: 'redProcessedResult' },
        { label: 'ブラウザ情報', path: this.getAttrValue, attrId: 'redDeviceInfo' },
        { label: 'IPアドレス', path: this.getAttrValue, attrId: 'redIpAddress' },
        { label: 'ルール説明文', path: this.getAttrValue, attrId: 'redRuleDescription' },
        { label: 'エラーメッセージ', path: this.getAttrValue, attrId: 'redErrorMessage' }
      ]
    },
    requestKeyMap () {
      return [
        { attr: 'redResultReviewed', request: 'reviewedResult', type: 'int' },
        { attr: 'redResultReviewedMemo', request: 'reviewedMemo' },
        { attr: 'redBlack', request: 'isBlack', type: 'int' }
      ]
    },
    requestBody () {
      let requestBody = {}
      if (!this.opluxData.node || !this.replicaData.node) return requestBody
      this.requestKeyMap.forEach(key => {
        const oldAttrValue = (_.find(this.opluxData.node.attributes, { attrId: key.attr }) || {}).attrValue
        const newAttrValue = (_.find(this.replicaData.node.attributes, { attrId: key.attr }) || {}).attrValue
        if (oldAttrValue !== newAttrValue) {
          if (!oldAttrValue && !newAttrValue) {
          // 変更前データ無し && 新データ未設定場合、リクエストに設定しない
          } else {
            requestBody[key.request] = key.type === 'int' ? Number(newAttrValue) : newAttrValue
          }
        }
      })
      return requestBody
    }
  },
  watch: {
    async visible (val) {
      this.dialogVisible = val
      if (val) {
        this.replicaData = _.cloneDeep(this.opluxData)
      }
    },
    dialogVisible (val) {
      this.$emit('update:visible', val)
    }
  },
  methods: {
    ...mapMutations('Order', {
      updateListItem: 'updateOpluxData'
    }),
    showErrorMessage (id) {
      return this.errorMessage(id)
    },
    errorMessage (id) {
      return this.errors.first(id, this.validateScope)
    },
    getAttrValue (prop) {
      const attrId = prop.attrId
      const attrObj = _.keyBy((this.replicaData.node || []).attributes, 'attrId')
      return (attrObj[attrId] || {}).attrValue
    },
    getSelectOptions (prop) {
      const attrId = prop.attrId
      switch (attrId) {
        case 'redResultReviewed':
          return [ { code: '10', name: 'OK' }, { code: '20', name: 'NG' }, { code: '30', name: 'HOLD' } ]
        case 'redBlack':
          return [ { code: '0', name: 'OFF' }, { code: '1', name: 'ON' } ]
        default:
          return []
      }
    },
    onChange (event, prop) {
      // 属性キーデータの存在を確認
      const hasAttr = this.replicaData.node.attributes.some(attr => attr.attrId === prop.attrId)
      if (hasAttr) {
      // 存在する場合、更新
        this.replicaData.node.attributes.forEach(attr => {
          if (attr.attrId === prop.attrId) {
            attr.attrValue = event
          }
        })
      } else {
      // 存在しない場合、追加
        this.replicaData.node.attributes.push({
          attrId: prop.attrId,
          attrValue: event
        })
      }
    },
    cancel () {
      this.replicaData = {}
      this.$validator.reset()
      this.dialogVisible = false
    },
    async ok () {
      await this.$validator.validate()
      if (this.hasError) {
        return
      }
      const url = config.OPLUX_EVENT_URL + '/' + this.uuid
      const heads = { 'X-CCS-Last-Updated-At': this.lastUpdatedAt }
      const data = this.requestBody
      try {
        this.$loadingService.show()
        const res = await common.apiAxios(url, { method: 'put', heads, data })
        this.updateListItem({ uuid: this.uuid, lsUpAt: res.data.lastUpdatedAt,
          rBody: this.requestBody, kMap: this.requestKeyMap })
        this.dialogVisible = false
        this.replicaData = {}
        this.$emit('requestUpdate') // requestUpdate が指定されていない場合は無視される
      } catch (e) {
        common.addError(e, { msg: message.MSG_553() })
        this.$notify.error({ error: e })
        throw e
      } finally {
        this.$loadingService.close()
      }
    }
  }
}
</script>
